import { ticketHappinessBonus } from './happiness.js';
import { boardingCheck } from './boarding-check.js';
import { CONFIG } from "./config.js";
import type { EconomyData, StationData, Reward, UpgradeChoice, VendingData, TerminalEconomyData, Wallet } from "./types.js";
import { tier, graduationAward, airlineMode } from "./airports.js";
import { CONTENT } from "../content/runtime.js";
// Generated curves preserve the original multiplication and rounding order.
const UPGRADE_POWERS = CONTENT.balance.tables.UPGRADE_POWERS, CAPACITY_POWERS = CONTENT.balance.tables.CAPACITY_POWERS;
const VENDING_POWERS = CONTENT.balance.tables.VENDING_POWERS;
const ARRIVAL_FRAMES = CONTENT.balance.tables.ARRIVAL_FRAMES;
const AMENITY_POWERS = CONTENT.balance.tables.AMENITY_POWERS, SHUTTLE_POWERS = CONTENT.balance.tables.SHUTTLE_POWERS;
const BOOTS_POWERS = CONTENT.balance.tables.BOOTS_POWERS, BAG_POWERS = CONTENT.balance.tables.BAG_POWERS, CLEANING_POWERS = CONTENT.balance.tables.CLEANING_POWERS;
/** Bigger airports cost more to build, staff and improve. */
export function costMult(e?: Pick<EconomyData, "run">): number { return e ? tier(e).costMult : 1; }
export function unlockCost(index: number, e?: Pick<EconomyData, "run">): number { return Math.ceil(CONFIG.stages[index].unlockCost * costMult(e)); }
/** Flexible helpers have one hiring price, regardless of their starting desk. */
/** The first hire is a fixed-desk clerk and a deliberate save-up goal; roamers cost real money. */
export const CLERK_COST = CONTENT.balance.constants.CLERK_COST;
export const HELPER_COST = CONTENT.balance.constants.HELPER_COST;
export function managerCost(_index: number, e?: Pick<EconomyData, "run">, hired = 0): number { return Math.ceil((hired > 0 ? HELPER_COST : CLERK_COST) * costMult(e)); }
export const MONEY_LIMIT = CONTENT.balance.constants.MONEY_LIMIT;
export const COSTS = CONTENT.balance.tables.COSTS;
export function capacity(station: StationData | number): number {
    if (typeof station === "number") return Math.min(4, 1 + Math.floor(station / 5));
    return station.index === 6 ? 1 : station.lanes;
}
/** Pass the economy to apply the current airport's desk/gate limits (a tiny airport has fewer). */
export function upgradeCost(s: StationData, choice: UpgradeChoice = "speed", e?: Pick<EconomyData, "run">): number {
    if (!s.unlocked) return Infinity;
    if (choice === "income" && ![0, 4, 5].includes(s.index)) return Infinity;
    const level = choice === "capacity" ? s.lanes - 1 : choice === "speed" ? s.speedLevel : s.incomeLevel;
    const lanes = e ? (s.index === 6 ? tier(e).maxGates : tier(e).maxLanes) : 4;
    const max = choice === "capacity" ? lanes - 1 : 20;
    return level >= max ? Infinity : Math.ceil(costMult(e) * CONFIG.stages[s.index].baseCost * (choice === "capacity" ? 2.5 : choice === "income" ? 1.3 : 1) * (choice === "capacity" ? CAPACITY_POWERS : UPGRADE_POWERS)[level]);
}
/** Cap the advertised and granted gain at the airport's actual remaining capacity. */
export function upgradeSteps(s: StationData, choice: UpgradeChoice, e: TerminalEconomyData, requested: number) {
    const level = choice === 'capacity' ? s.lanes : choice === 'speed' ? s.speedLevel : s.incomeLevel;
    const max = choice === 'capacity' ? s.index === 6 ? tier(e).maxGates : tier(e).maxLanes : 20;
    return Math.max(0, Math.min(requested, max - level));
}
export function serviceFrames(index: number, station: StationData | number, passengerSerial?: number): number {
    const level = typeof station === "number" ? Math.max(0, station - 1) : station.speedLevel;
    const passengerCheck = index === 6 && passengerSerial !== undefined;
    const frames = passengerCheck ? boardingCheck(passengerSerial).frames : CONFIG.stages[index].serviceFrames;
    return Math.max(passengerCheck ? 9 : 24, Math.round(frames / (1 + level * 0.18) / (1 + (typeof station === "number" ? 0 : station.dividerLevel || 0) * 0.05)));
}
/** Tickets, checked-bag fees, shop sales and paid lounge visits earn money. */
/** Legacy reputation still multiplies payouts until prestige economics are retired. */
export function payout(s: StationData, e: TerminalEconomyData, frame: number, prestige: number): number {
    if (![0, 1, 4, 5].includes(s.index)) return 0;
    return Math.floor(CONFIG.stages[s.index].income * tier(e).incomeMult * (1 + s.incomeLevel * 0.25) * (s.index === 0 ? ticketHappinessBonus(e.passengerHappiness) : 1) * (s.index === 5 ? chairBoost(e) : 1) * (1 + prestige * 0.1) * (e.incomeUntil > frame ? 2 : 1));
}
/** International travellers pay this at Passport Control (MGT-009); domestic travellers never pass it. */
export function passportFee(e: Pick<EconomyData, 'run'>): number { return Math.floor(CONFIG.stages[3].income * tier(e).incomeMult); }
/** Comfortable seating improves the value of a paid lounge visit. */
export function chairBoost(e: Pick<EconomyData, "chairLevel">): number { return 1 + (e.chairLevel || 0) * 0.04; }
/** Business revenue is earned here but remains on the floor until a captain picks it up. */
/** A visiting departure pays half the fare per seat filled, the whole fare when every seat is full. */
export function departurePayout(boarded: number, seats: number, fare: number): number {
    if (boarded <= 0 || fare <= 0) return 0;
    return Math.round(boarded * fare * (boarded >= seats ? 1 : 0.5));
}
export function deposit(e: Pick<EconomyData, 'earned'>, target: { pending: number }, amount: number): number {
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    const n = Math.min(Math.floor(amount), MONEY_LIMIT - target.pending);
    target.pending += n;
    e.earned = Math.min(MONEY_LIMIT, e.earned + n);
    return n;
}
export function vendingCost(v: VendingData, choice: "income" | "speed"): number {
    const level = choice === "speed" ? v.speedLevel : v.level - 1;
    return level >= 10 ? Infinity : Math.ceil((choice === "speed" ? 90 : 75) * VENDING_POWERS[level]);
}
export function vendingReady(v: Pick<VendingData, 'sold' | 'level' | 'speedLevel'>): boolean {
    return Math.floor(v.sold / 10) > v.level - 1 + v.speedLevel;
}
export function vendingPayout(v: VendingData, e: TerminalEconomyData, frame: number, prestige: number): number {
    return Math.floor((8 + v.index * 2) * tier(e).incomeMult * (1 + (v.level - 1) * 0.3) * (1 + prestige * 0.1) * (e.incomeUntil > frame ? 2 : 1));
}
/** Invalid upgrade levels use the unupgraded cadence rather than speeding up arrivals. */
export function arrivalFrames(level: number): number { return Number.isInteger(level) && level >= 0 ? ARRIVAL_FRAMES[level] ?? CONTENT.balance.constants.MIN_ARRIVAL_FRAMES : CONFIG.arrivalFrames; }
export function specialCost(kind: string, e: TerminalEconomyData): number { const n = baseSpecialCost(kind, e); return Number.isFinite(n) ? Math.ceil(n * costMult(e)) : n; }
function baseSpecialCost(kind: string, e: TerminalEconomyData): number {
    if (kind === "chairs") return e.chairLevel >= 5 ? Infinity : Math.ceil(50 * AMENITY_POWERS[e.chairLevel]);
    if (kind === "shuttle")
        return e.arrivalLevel >= 10 ? Infinity : Math.ceil(160 * SHUTTLE_POWERS[e.arrivalLevel]);
    if (kind === "boots")
        return e.boots >= 5 ? Infinity : Math.ceil(100 * BOOTS_POWERS[e.boots]);
    if (kind === "bags")
        return e.bagLevel >= 4 ? Infinity : Math.ceil(120 * BAG_POWERS[e.bagLevel]);
    if (kind === "cleaning") return e.trashLevel >= 4 ? Infinity : Math.ceil(80 * CLEANING_POWERS[e.trashLevel]);
    return Infinity;
}
/** Credit the career wallet; earned money also counts toward this terminal's own earned total. */
export function addMoney(wallet: Pick<Wallet, 'money'>, e: Pick<EconomyData, 'earned'>, amount: number, earned = true): number {
    if (!Number.isFinite(amount) || amount <= 0)
        return 0;
    const n = Math.min(Math.floor(amount), MONEY_LIMIT - wallet.money);
    wallet.money += n;
    if (earned)
        e.earned = Math.min(MONEY_LIMIT, e.earned + n);
    return n;
}
export function debit(wallet: Pick<Wallet, 'money'>, amount: number): boolean {
    if (!Number.isFinite(amount) || amount < 0 || !Number.isInteger(amount) || wallet.money < amount)
        return false;
    wallet.money -= amount;
    return true;
}
/** Moving to the next airport. See airports.ts for the requirements. */
export function prestigeAward(e: TerminalEconomyData, stations: StationData[]): number { return graduationAward(e, stations); }
export interface RadioAccount { radioCooldown: number; }
export const RADIO_RECHARGE = CONTENT.balance.constants.RADIO_RECHARGE;
export const RADIO_DURATION = CONTENT.balance.constants.RADIO_DURATION;
export function rewardAvailable(e: TerminalEconomyData, kind: Reward, frame: number, account?: RadioAccount): boolean {
    if (kind === 'offline' || kind === 'income' && e.incomeUntil > frame || kind === 'express' && e.expressUntil > frame)
        return false;
    return frame >= (account?.radioCooldown ?? Math.max(e.incomeCooldown, e.cashCooldown, e.expressCooldown));
}
/** Supply-limited unboosted income estimate until the calibrated Casual harness exists. */
export function fundingReward(e: TerminalEconomyData, stations: StationData[], frame: number, prestige: number): number {
    const open = stations.filter(s => s.unlocked);
    if (!open.length) return 0;
    const port = tier(e), gates = stations[6]?.lanes ?? 1;
    const supply = airlineMode(e) ? e.pilotCount * 8 * 60 / 3600 : gates * port.seats * 60 / (port.turnaround + 360 + port.seats * 150);
    const rate = Math.min(supply, ...open.map(s => capacity(s) * 60 / serviceFrames(s.index, s)));
    return Math.floor(60 * rate * open.reduce((sum, s) => sum + payout(s, { ...e, incomeUntil: 0 }, frame, prestige), 0));
}
export function offlineBonus(wallet: Wallet, e: TerminalEconomyData, stations: StationData[], frame: number): number {
    return Math.min(Math.floor(wallet.offlineAward * .25), fundingReward(e, stations, frame, wallet.prestige) * 5);
}
export function consumeRadioCharge(e: TerminalEconomyData, frame: number, account?: RadioAccount): void {
    if (account) account.radioCooldown = frame + RADIO_RECHARGE;
    else e.incomeCooldown = e.cashCooldown = e.expressCooldown = frame + RADIO_RECHARGE;
}
export function applyReward(e: TerminalEconomyData, wallet: Wallet, stations: StationData[], kind: Reward, frame: number, account?: RadioAccount): boolean {
    if (!rewardAvailable(e, kind, frame, account))
        return false;
    if (kind === 'income') {
        e.incomeUntil = frame + RADIO_DURATION;
    }
    if (kind === 'express') {
        e.expressUntil = frame + RADIO_DURATION;
    }
    if (kind === 'cash') {
        addMoney(wallet, e, fundingReward(e, stations, frame, wallet.prestige));
    }
    if (kind === 'offline') {
        deposit(e, stations[0], offlineBonus(wallet, e, stations, frame));
        wallet.offlineClaimed = true;
    }
    if (kind !== 'offline') consumeRadioCharge(e, frame, account);
    e.puzzles++;
    return true;
}

export function dividerCost(s: StationData, e?: Pick<EconomyData, "run">): number { return s.dividerLevel >= 5 ? Infinity : Math.ceil(costMult(e) * CONFIG.stages[s.index].baseCost * 0.6 * AMENITY_POWERS[s.dividerLevel]); }
export const SPECIAL_TIMERS = { chairs: 'chairUntil', boots: 'bootsUntil', bags: 'bagsUntil', cleaning: 'cleaningUntil', shuttle: 'shuttleUntil' } as const;
